/**
 * OmegaTopPage — L1 of the OMEGA dashboard.
 *
 * Composes three sections in this order (spec §3 — top-level):
 *
 *   1. AlertsStrip          — render only when alerts > 0
 *   2. OmegaStatusSection   — alive/killed, root_id, born, stance
 *   3. BrainStatusGrid      — 6 brain tiles (auto-flow on growth)
 *
 * Owns the polling subscription via `useOmegaState` /
 * `useBrainSummaries`. Renders a quiet skeleton on first load so
 * the page chrome doesn't flash.
 */

import { Link } from 'react-router-dom';
import { useBrainSummaries, useOmegaState } from '../hooks/useOmegaState';
import { AlertsStrip } from '../sections/AlertsStrip';
import { BrainStatusGrid } from '../sections/BrainStatusGrid';
import { OmegaStatusSection } from '../sections/OmegaStatusSection';
import { OmegaProposalsTile } from '../tiles/OmegaProposalsTile';
import { DecisionsTile } from '../tiles/DecisionsTile';
import { OmegaMemoryTile } from '../tiles/OmegaMemoryTile';
import { OmegaCeoGiTile } from '../tiles/OmegaCeoGiTile';
import { KnowledgePainPointsTile } from '../tiles/KnowledgePainPointsTile';
import { LearningView } from '../../LearningView';

/** Lightweight shimmer for the status card while `/omega/state` is in flight, so the
 *  page chrome (nav + tiles) paints instantly instead of blocking on the fetch. */
function OmegaStatusSkeleton() {
  return (
    <div className="animate-pulse rounded-lg border border-border bg-surface p-4">
      <div className="h-4 w-40 rounded bg-surface-raised" />
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 rounded bg-surface-raised" />
        ))}
      </div>
    </div>
  );
}

export function OmegaTopPage() {
  const stateQuery = useOmegaState();
  const brainsQuery = useBrainSummaries();

  // Never block the whole page on the state fetch — the nav + the self-fetching
  // tiles render immediately; only the status card waits (with a skeleton).
  const state = stateQuery.data;
  const brains = brainsQuery.data ?? [];

  // Sum active alerts across brains for the strip. Server-side this
  // will be a single count from /api/v1/omega/alerts; until then the
  // sum across BrainSummary is the right approximation.
  const alertCount = brains.reduce((acc, b) => acc + b.active_alerts, 0);

  return (
    <>
      <AlertsStrip count={alertCount} />
      <div className="flex flex-wrap gap-2 pb-2">
        <Link
          to="/omega/console"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Console ★
        </Link>
        <Link
          to="/omega/briefing"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Daily Briefing
        </Link>
        <Link
          to="/omega/launchplans"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Launch Plans
        </Link>
        <Link
          to="/omega/commander"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Commander ★
        </Link>
        <Link
          to="/omega/pipeline"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Pipeline
        </Link>
        <Link
          to="/omega/research/teams"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Research
        </Link>
        <Link
          to="/omega/marketing"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Marketing
        </Link>
        <Link
          to="/omega/sales"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Sales
        </Link>
        <Link
          to="/omega/customers"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Customers
        </Link>
        <Link
          to="/omega/decisions"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Decisions
        </Link>
        <Link
          to="/omega/ceo"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          CEO
        </Link>
        <Link
          to="/omega/market-intel"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Market Intel
        </Link>
        <Link
          to="/omega/activity"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Activity
        </Link>
        <Link
          to="/omega/finance"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-raised"
        >
          Finance
        </Link>
      </div>
      {state ? <OmegaStatusSection state={state} /> : <OmegaStatusSkeleton />}
      <BrainStatusGrid brains={brains} isLoading={brainsQuery.isPending && !brainsQuery.data} />
      <OmegaCeoGiTile />
      <OmegaProposalsTile />
      <DecisionsTile />
      <OmegaMemoryTile />
      <LearningView />
      <KnowledgePainPointsTile />
    </>
  );
}
